/*
 *  Agent-posture label badge for Fresh listings.
 *
 *  The backend (app/services/fresh_labels) reads every owner-posted listing's
 *  text and records what it says about agents. Mirrors the five
 *  ``agent_label*`` fields on FreshListingOut. "none" never reaches the UI —
 *  the API folds it into null.
 *
 *  Two variants: ``badge`` matches the photo-corner badge row on FreshCard
 *  (rounded-md, uppercase, solid fill); ``inline`` is the text-only form for
 *  the Deck's seller line, which avoids theme-mapped tokens.
 */

export type AgentLabel = "no_agents" | "likely_agent" | "agents_welcome";
export type AgentLabelSource = "regex" | "phrase" | "gemini" | "human";

export interface AgentLabelFields {
  agent_label: AgentLabel | null;
  agent_label_confidence: number | null;
  agent_label_reason: string | null;
  agent_label_evidence: string | null;
  agent_label_source: AgentLabelSource | null;
}

export const AGENT_LABEL_META: Record<
  AgentLabel,
  { text: string; badge: string; inline: string; long: string }
> = {
  no_agents: {
    text: "No agents",
    badge: "bg-rose-600 text-white ring-1 ring-rose-300/40",
    inline: "text-rose-400",
    long: "Owner says agents are not welcome",
  },
  likely_agent: {
    text: "Likely agent",
    // Same hue family as the undeclared-agency badge (both mean "not really an
    // owner") but outlined so the two stay distinguishable side by side.
    badge: "bg-orange-950/80 text-orange-200 ring-1 ring-orange-400/70",
    inline: "text-orange-400/90",
    long: "Text reads like an agent posting as owner",
  },
  agents_welcome: {
    text: "Agents welcome",
    // emerald-700, not -500: the "New" badge already uses emerald-500.
    badge: "bg-emerald-700 text-white ring-1 ring-emerald-300/40",
    inline: "text-emerald-400/90",
    long: "Owner explicitly welcomes agents",
  },
};

const SOURCE_NOTE: Record<AgentLabelSource, string> = {
  regex: "pattern match (AI unavailable)",
  phrase: "taught phrase",
  gemini: "AI",
  human: "confirmed by a teammate",
};

export function agentLabelTitle(f: AgentLabelFields): string {
  const meta = f.agent_label ? AGENT_LABEL_META[f.agent_label] : undefined;
  if (!meta) return "";
  const parts: string[] = [meta.long];
  if (f.agent_label_reason) parts.push(f.agent_label_reason);
  if (f.agent_label_evidence) parts.push(`“${f.agent_label_evidence}”`);
  const tail: string[] = [];
  if (f.agent_label_source) tail.push(SOURCE_NOTE[f.agent_label_source]);
  if (f.agent_label_confidence != null && f.agent_label_source !== "human") {
    tail.push(`${Math.round(f.agent_label_confidence * 100)}%`);
  }
  if (tail.length) parts.push(tail.join(" · "));
  return parts.join("\n");
}

export function AgentLabelBadge({
  fields,
  variant = "badge",
  className = "",
}: {
  fields: AgentLabelFields;
  variant?: "badge" | "inline";
  className?: string;
}) {
  const label = fields.agent_label;
  // Defensive: an unknown value (e.g. a stray "none") renders nothing instead
  // of throwing inside render and blanking the whole page.
  const meta = label ? AGENT_LABEL_META[label] : undefined;
  if (!meta) return null;
  const title = agentLabelTitle(fields);
  if (variant === "inline") {
    return (
      <span
        className={`text-[9.5px] uppercase tracking-wider font-semibold ${meta.inline} ${className}`}
        title={title}
      >
        {meta.text}
        {fields.agent_label_source === "human" ? " ✓" : ""}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm ${meta.badge} ${className}`}
      title={title}
    >
      {meta.text}
      {fields.agent_label_source === "human" && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-label="confirmed by a teammate">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </span>
  );
}
